import PropTypes from 'prop-types';
import { ui, dia, linkTools, util } from '@joint/plus';
import { getTheme } from '../../../../theme';
import { inspectorDefinitions } from '../../config/inspector';
import getSpecificProperties from './getSpecificProperties';
import { createFieldRenderer } from './renderers/index';
import {
  getInitialValue,
  handlePortChange,
  updateCellProperty,
  updatePortLinks,
  updatePorts,
  addOutageEntry,
  removeOutageEntry,
  refreshOutagesEntity,
} from './utils/InspectorUtils';

export class InspectorService {
  constructor({
    openGroupsButton,
    closeGroupsButton,
    container,
    header,
    content,
    isDarkThemeUsed,
    closeInspector,
    readOnly = false,
  }) {
    this.theme = getTheme(isDarkThemeUsed);
    this.closeInspector = closeInspector;

    if (openGroupsButton) {
      openGroupsButton.addEventListener('click', () => this.inspector?.openGroups());
    }
    if (closeGroupsButton) {
      closeGroupsButton.addEventListener('click', () => this.inspector?.closeGroups());
    }

    this.container = container;
    this.header = header;
    this.content = content;
    this.modules = [];
    this.inspector = null;
    this.graph = null;
    this.paper = null;
    this.readOnly = readOnly;

    // internal listeners we might rebind across create() calls
    this._onCellNameChange = this._onCellNameChange.bind(this);
    this._onCustomAttrChange = null;
    this._modulesRemoveListener = null;
  }

  removeIfOpen() {
    if (this.inspector) {
      this.inspector.remove();
      this.inspector = null;
    }

    // Clean up link cell listener if it exists
    if (this._currentLinkCell && this._linkLengthHandler) {
      this._currentLinkCell.off('change:vertices change:source change:target', this._linkLengthHandler);
      this._currentLinkCell = null;
      this._linkLengthHandler = null;
    }

    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  setModules(newModules) {
    this.modules = newModules;
  }

  getInitialValue(field) {
    const cell = this.graph.getCell(field.cellId);
    if (!cell) {
      console.warn('[InspectorService] Cell not found for field:', field);
      return;
    }
    return getInitialValue(this.graph, field);
  }

  updateCellProperty(field, newValue, options = {}) {
    updateCellProperty(this.graph, field, newValue, options);
  }

  findOutageIndex(graph, cellId) {
    if (!graph.tables || !Array.isArray(graph.tables.outages)) return -1;
    return graph.tables.outages.findIndex((o) => o.cellId === cellId);
  }

  updateOutageField(cellId, _, fieldName, newValue) {
    if (!this.graph.tables) this.graph.tables = {};
    if (!Array.isArray(this.graph.tables.outages)) this.graph.tables.outages = [];

    const outages = this.graph.tables.outages;
    const existingOutage = outages.find((o) => o.cellId === cellId);

    if (!existingOutage) {
      const newOutage = {
        cellId,
        entity: '',
        type: '',
        impact: 1000,
        startTime: 0,
        endTime: 0,
        [fieldName]: newValue,
      };
      outages.push(newOutage);
    } else {
      Object.assign(existingOutage, { [fieldName]: newValue });
    }
  }

  addOutage(cellId, outage = {}) {
    addOutageEntry(this.graph, cellId, outage);
  }

  removeOutage(cellId, index) {
    removeOutageEntry(this.graph, cellId, index);
  }

  create(cell, graph, paper, modules) {
    // teardown previous inspector and its cell listeners
    if (this.inspector) {
      this.inspector.remove();
      this.inspector = null;
    }

    // Clean up previous link cell listener if it exists
    if (this._currentLinkCell && this._linkLengthHandler) {
      this._currentLinkCell.off('change:vertices change:source change:target', this._linkLengthHandler);
      this._currentLinkCell = null;
      this._linkLengthHandler = null;
    }

    this.container.innerHTML = '';

    // assign graph and paper
    this.graph = graph;
    this.paper = paper;
    this.modules = modules;

    // ensure handler is not duplicated
    graph.off('change:cellName', this._onCellNameChange);
    graph.off('change:customAttributes/cellName', this._onCellNameChange);

    // listen to name changes
    graph.on('change:cellName', this._onCellNameChange);
    graph.on('change:customAttributes/cellName', this._onCellNameChange);

    if (this._modulesRemoveListener) {
      this.graph.off('remove', this._modulesRemoveListener);
      this.graph.off('change:customAttributes/module', this._modulesRemoveListener);
    }

    const type = cell.get('type');
    const inspectorConfig = this.getInspectorConfig()[type];
    if (!inspectorConfig) {
      console.warn('[InspectorService] No inspector configuration found for type:', type);
      return;
    }
    if (
      cell.isElement() &&
      (cell.get('customAttributes/cellName') === undefined || cell.get('customAttributes/cellName') === 'undefined')
    ) {
      cell.prop('customAttributes/cellName', '');
    }

    const subType = cell.get('customAttributes')?.fullName?.replace('_', '') || '';
    const finalConfig = util.cloneDeep(inspectorConfig);

    if (subType && type === 'standard.Rectangle') {
      const specificProps = getSpecificProperties(subType);
      if (specificProps) {
        finalConfig.inputs = {
          ...finalConfig.inputs,
          ...specificProps,
        };
      }
    }

    const groups = Object.entries(finalConfig.groups).reduce((acc, [key, group]) => {
      acc[key] = {
        ...group,
        attributes: {
          'data-cy': `${key}-group`,
        },
      };
      return acc;
    }, {});

    const enhancedInputs = Object.entries(finalConfig.inputs).reduce((acc, [key, input]) => {
      acc[key] = {
        ...util.cloneDeep(input),
        cellId: cell.id,
        readOnly: this.readOnly || input.readOnly === true,
        attributes: {
          'data-cy': `${key}-input`,
        },
      };
      return acc;
    }, {});

    this.container.setAttribute('data-cy', 'inspector-container');
    this.content.setAttribute('data-cy', 'inspector-content');

    this.inspector = new ui.Inspector({
      cell,
      inputs: enhancedInputs,
      groups,
      container: this.container,
      renderFieldContent: (field, path) =>
        createFieldRenderer({ ...field, readOnly: this.readOnly || field.readOnly }, path, this),
      liveUpdate: true,
      validateCell: (c) => !c.isLink(),
      cellHeight: 48,
    });

    if (!this.inspector) {
      console.error('[InspectorService] Failed to create inspector.');
      return;
    }

    this.inspector.on('close', this.closeInspector);
    this.container.appendChild(this.inspector.render().el);

    if (cell.isLink()) {
      const initialLength = cell.calculateLength();
      cell.set('length', initialLength);

      // Store reference for cleanup
      this._currentLinkCell = cell;
      this._linkLengthHandler = () => {
        const newLength = cell.calculateLength();
        cell.set('length', newLength);
        if (this.inspector) this.inspector.render();
      };
      cell.on('change:vertices change:source change:target', this._linkLengthHandler);

      if (this.paper) {
        const cellView = this.paper.findViewByModel(cell);
        if (cellView && !cellView.hasTools('link-tools')) {
          const toolsView = new dia.ToolsView({
            name: 'link-tools',
            tools: [new linkTools.Vertices({ vertexAdding: true }), new linkTools.Boundary({ padding: 15 })],
          });
          cellView.addTools(toolsView);
          toolsView.el.style.pointerEvents = 'all';
        }
      }
    }

    this.inspector.on('change', () => {
      this.inspector.render();
      paper.trigger('cell:changed', cell);
    });

    return this.inspector;
  }

  _onCellNameChange(cell) {
    refreshOutagesEntity(this.graph, cell.id);
    this.graph.trigger('change:tables', this.graph, this.graph.tables, { ignoreCommandManager: true });
  }

  getInspectorConfig() {
    return inspectorDefinitions;
  }

  updatePorts(newPorts, cellId) {
    updatePorts(this.graph, newPorts, cellId);
  }

  handlePortChange(portId, fieldName, newValue, ports, cellId) {
    handlePortChange(this, portId, fieldName, newValue, ports, cellId);
  }

  updatePortLinks(port, cellId) {
    updatePortLinks(this.graph, port, cellId);
  }
}

InspectorService.propTypes = {
  openGroupsButton: PropTypes.object,
  closeGroupsButton: PropTypes.object,
  container: PropTypes.object.isRequired,
  header: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  isDarkThemeUsed: PropTypes.bool,
  closeInspector: PropTypes.func,
  readOnly: PropTypes.bool,
};
